/**
 * Asserts to Typescript that a given property exists on an object.
 * In some cases, the second of these two examples will throw a compiler error, even though it's technically a "safe" operation.
 * Warning, this does _not_ technically check the property is truthy, just that the object has the property.
 * @example if (isKeyOf("a", obj)) ? obj[a] : "fallback";
 * @example return obj[a] ?? "fallback";
 */
export const isKeyOf = <T extends Record<string, unknown>>(
  obj: T,
  potentialKey: string
): potentialKey is keyof T & string => {
  return Object.hasOwn(obj, potentialKey);
};

/**
 * Mark any given fields marked as optional on a type as required, but leave all other properties as optional.
 * For example, WorkingFilters is a Partial but we may want to enforce date and duration when setting Reserve filters.
 * @example type WithRequiredFields<WorkingFilters, "date"" | "duration">
 */
export type WithRequiredFields<T, K extends keyof T> = T & Required<Pick<T, K>>;

/**
 * Asserts that a given value is a member of an object.
 * Useful for asserting that something is included in a keyed list of (readonly) constants values.
 */
export const isValueOf = <V, T extends Record<string, V>>(obj: T, potentialValue: V): potentialValue is T[keyof T] => {
  return Object.values(obj).includes(potentialValue);
};
